import { useState, useEffect } from "react";
import {
  ChevronRight,
  X,
  CheckCircle2,
  Circle,
  AlertCircle,
  BugPlayIcon,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Workflow, StageName } from "@butler/shared";
import { api } from "@/api";
import { DevChat } from "../Chat/DevChat";
import { WorkflowFunctions } from "./WorkflowFunctions";
import { WorkflowTriggers } from "./WorkflowTriggers";
import { WorkflowTools } from "./WorkflowTools";
import { Sheet, SheetContent } from "../ui/sheet";
import { useWorkflow } from "@/lib/useWorkflow";
export function WorkflowCard({ id }: { id: string }) {
  const workflow = useWorkflow();
  if (!workflow) return null;
  return <AgentProgressCard workflow={workflow} />;
}

interface AgentProgressCardProps {
  workflow: Workflow;
  onClose?: () => void;
}

// Utility functions
const getStageStatus = (
  stage: StageName,
  workflow: Workflow,
  stages: StageName[],
) => {
  const currentIndex = stages.indexOf(workflow.current_stage);
  const stageIndex = stages.indexOf(stage);

  if (stageIndex < currentIndex) return "completed";
  if (stageIndex === currentIndex) return "current";
  return "upcoming";
};

const getStageIcon = (
  stage: StageName,
  workflow: Workflow,
  stages: StageName[],
) => {
  const status = getStageStatus(stage, workflow, stages);

  if (status === "completed")
    return <CheckCircle2 className="h-5 w-5 text-green-500" />;
  if (status === "current")
    return <AlertCircle className="h-5 w-5 text-blue-500 animate-pulse" />;
  return <Circle className="h-5 w-5 text-gray-300" />;
};

// Collapsed view component
const CollapsedView = ({
  workflow,
  stages,
  onExpand,
}: {
  workflow: Workflow;
  stages: StageName[];
  onExpand: () => void;
}) => (
  <Card
    className={cn(
      "h-full overflow-hidden transition-all duration-300 gap-4",
      "rounded-lg cursor-pointer hover:shadow-md",
    )}
    onClick={onExpand}
  >
    <CardHeader className="text-xs text-gray-500 whitespace-nowrap flex items-center justify-between">
      {workflow.name || `Workflow: ${workflow.ID.substring(0, 8)}`}
      <ChevronRight className="h-5 w-5 text-gray-400" />
    </CardHeader>
    <CardContent className="flex items-center space-x-1 mr-4">
      {stages.map((stage) => (
        <div key={stage} className="flex-1 flex flex-col items-center">
          <div
            className={cn(
              "h-1 w-full",
              getStageStatus(stage, workflow, stages) === "completed"
                ? "bg-green-500"
                : getStageStatus(stage, workflow, stages) === "current"
                  ? "bg-blue-500"
                  : "bg-gray-200 dark:bg-gray-700",
            )}
          />
          {/* {getStageStatus(stage, workflow, stages) === "current" && (
            <span className="mt-1 text-[10px] text-blue-600 dark:text-blue-400 font-medium">
              {stage}
            </span>
          )} */}
        </div>
      ))}
    </CardContent>
  </Card>
);

// Expanded view component
const ExpandedView = ({
  workflow,
  open,
  stages,
  onClose,
}: {
  workflow: Workflow;
  stages: StageName[];
  open: boolean;
  onClose: () => void;
}) => {
  const [devLogsOpen, setDevLogsOpen] = useState(false);
  const toggleDevLogs = () => setDevLogsOpen(!devLogsOpen);
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="px-8 py-4 min-w-2xl overflow-scroll">
        <div className="flex flex-row items-center space-y-0 pb-2">
          <div className="">
            <CardTitle className="text-xl font-bold">Agent Workflow</CardTitle>
            <CardDescription>{workflow.name}</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDevLogs}
            className="size-8"
          >
            <BugPlayIcon className="text-red-400 size-3" />
          </Button>
        </div>
        {devLogsOpen ? (
          <DevChat id={workflow.ID} />
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Progress</h3>
              <div className="space-y-1">
                {stages.map((stage, index) => (
                  <div key={stage} className="flex items-start py-1">
                    <div className="mr-3 mt-0.5">
                      {getStageIcon(stage, workflow, stages)}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <p
                          className={cn(
                            "text-sm font-medium",
                            getStageStatus(stage, workflow, stages) ===
                              "current"
                              ? "text-blue-600 dark:text-blue-400"
                              : getStageStatus(stage, workflow, stages) ===
                                  "completed"
                                ? "text-green-600 dark:text-green-400"
                                : "text-gray-500",
                          )}
                        >
                          {stage}
                        </p>
                        {getStageStatus(stage, workflow, stages) ===
                          "current" && (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                        )}
                      </div>
                      {/* <p className="text-xs text-gray-500">
                        {getStageStatus(stage, workflow, stages) === "current"
                          ? `Agent is currently in the ${stage.toLowerCase()} stage`
                          : getStageStatus(stage, workflow, stages) ===
                            "completed"
                          ? `${stage} stage completed`
                          : `Waiting to start ${stage.toLowerCase()} stage`}
                      </p> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-medium">Workflow Details</h3>
              <div className="rounded-md border p-4 space-y-4">
                <div>
                  <h4 className="text-xs font-medium text-gray-500">
                    CREATED BY
                  </h4>
                  <p className="text-sm">{workflow.created_by}</p>
                </div>
                <div>
                  <h4 className="text-xs font-medium text-gray-500">
                    CREATED AT
                  </h4>
                  <p className="text-sm">
                    {workflow.created_at.toLocaleString()}
                  </p>
                </div>
                <div>
                  <h4 className="text-xs font-medium text-gray-500">STATUS</h4>
                  <Badge variant={workflow.active ? "default" : "secondary"}>
                    {workflow.active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-medium">Triggers</h3>
              <WorkflowTriggers workflow={workflow} />
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-medium">Tools</h3>
              <WorkflowTools workflow={workflow} />
            </div>
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Functions</h3>
              <WorkflowFunctions workflow={workflow} />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

function AgentProgressCard({ workflow, onClose }: AgentProgressCardProps) {
  const [expanded, setExpanded] = useState(false);

  if (!workflow) return null;

  const stages: StageName[] = [
    "Request",
    "Requirements",
    "Planning",
    "Implementing",
    "Testing",
    "Running",
    "Updating",
  ];

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      {/* Always show collapsed view */}
      <div
        className={cn(
          "transition-all duration-300 ease-in-out",
          "w-64 h-auto",
          expanded && "opacity-50",
        )}
      >
        <CollapsedView
          workflow={workflow}
          stages={stages}
          onExpand={toggleExpand}
        />
      </div>

      {/* Show expanded view when expanded */}
      <ExpandedView
        open={expanded}
        workflow={workflow}
        stages={stages}
        onClose={toggleExpand}
      />
    </>
  );
}
